import { useState, useRef } from 'react';
import { compressImageFile } from '../../utils/imageUtils';
import { getDistanceUnit, distanceLabelFull } from '../../utils/units';
import PhotoPickerModal from '../PhotoPickerModal';

const currentYear = new Date().getFullYear();
const years = Array.from({ length: currentYear - 1910 + 1 }, (_, i) => currentYear - i);

export default function StepVehicle({ data, update, onNext, isFirstTime }) {
  const fileRef = useRef(null);
  const [showPhotoPicker, setShowPhotoPicker] = useState(false);

  // Mileage is always required (it's the current odometer reading). TMU is just a flag.
  const canProceed = data.year && data.make && data.model && data.mileage;

  return (
    <>
      <div className="screen-header">
        <h1 className="screen-title">{isFirstTime ? 'Add Your First Vehicle' : 'Add a Vehicle'}</h1>
        <p className="screen-subtitle">
          {isFirstTime
            ? "Add your first vehicle to get started. Don\u2019t worry \u2014 you can add more later from your garage."
            : 'Enter your vehicle details below.'}
        </p>
      </div>

      <div style={{ flex: 1, paddingTop: 24 }}>
        <div className="form-group">
          <label className="form-label">Year</label>
          <select
            className="form-select"
            value={data.year}
            onChange={(e) => update({ year: e.target.value })}
          >
            <option value="">Year</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Make</label>
          <input
            className="form-input"
            type="text"
            placeholder="e.g. Toyota, Honda, BMW"
            value={data.make}
            onChange={(e) => update({ make: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Model & Trim</label>
          <input
            className="form-input"
            type="text"
            placeholder="e.g. Camry XSE, Civic Si, M3 Competition"
            value={data.model}
            onChange={(e) => update({ model: e.target.value })}
          />
        </div>

        {/* Distance Units Toggle */}
        <div className="form-group">
          <label className="form-label">Distance Units</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {['miles', 'km'].map((unit) => {
              const selected = (data.distance_units || getDistanceUnit()) === unit;
              return (
                <button
                  key={unit}
                  type="button"
                  onClick={() => update({ distance_units: unit })}
                  style={{
                    padding: '8px 20px',
                    borderRadius: 100,
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    border: `1.5px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
                    background: selected ? 'var(--accent-subtle)' : 'transparent',
                    color: selected ? 'var(--accent)' : 'var(--text-secondary)',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {unit === 'miles' ? 'Miles' : 'Km'}
                </button>
              );
            })}
          </div>
        </div>

        {/* Odometer Reading + TMU flag */}
        <div className="form-group">
          <label className="form-label">{`Current Odometer Reading (${distanceLabelFull(data.distance_units)})`}</label>
          <input
            className="form-input"
            type="number"
            inputMode="numeric"
            placeholder={(data.distance_units || getDistanceUnit()) === 'km' ? 'e.g. 39,400' : 'e.g. 24,500'}
            value={data.mileage}
            onChange={(e) => update({ mileage: e.target.value })}
          />
          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              cursor: 'pointer',
              marginTop: 12,
            }}
          >
            <input
              type="checkbox"
              checked={data.tmu || false}
              onChange={(e) => update({ tmu: e.target.checked })}
              style={{
                width: 18,
                height: 18,
                accentColor: 'var(--warning)',
                flexShrink: 0,
                marginTop: 1,
              }}
            />
            <div>
              <span style={{ fontSize: '0.875rem', color: data.tmu ? 'var(--warning)' : 'var(--text-secondary)', fontWeight: 500 }}>
                Total Mileage Unknown (TMU)
              </span>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.6875rem', marginTop: 3, lineHeight: 1.4 }}>
                Check if the odometer has been replaced, rolled back, or you&apos;re unsure of the true total mileage
              </p>
            </div>
          </label>
        </div>

        <div className="form-group">
          <label className="form-label">VIN (Optional)</label>
          <input
            className="form-input"
            type="text"
            maxLength={17}
            placeholder="17-character vehicle identification number"
            value={data.vin || ''}
            onChange={(e) => update({ vin: e.target.value.toUpperCase() })}
            style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', letterSpacing: '0.05em' }}
          />
        </div>

        {/* Optional vehicle photo with camera capture */}
        <div className="form-group">
          <label className="form-label">Vehicle Photo (Optional)</label>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              try {
                const compressed = await compressImageFile(file, 800, 0.8);
                update({ photo: compressed });
              } catch {
                // silently fail
              }
              e.target.value = '';
            }}
          />
          {data.photo ? (
            <button
              type="button"
              onClick={() => setShowPhotoPicker(true)}
              style={{
                width: '100%',
                height: 140,
                borderRadius: 'var(--radius-sm)',
                background: `url(${data.photo}) center/cover no-repeat`,
                border: '1.5px solid var(--border)',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div style={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
              </div>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setShowPhotoPicker(true)}
              style={{
                width: '100%',
                height: 80,
                borderRadius: 'var(--radius-sm)',
                border: '1.5px dashed var(--border)',
                background: 'var(--bg-input)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                color: 'var(--text-muted)',
                fontSize: '0.8125rem',
                cursor: 'pointer',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              Add a photo of your vehicle
            </button>
          )}
        </div>
      </div>

      <div className="nav-row">
        <button className="btn btn-primary" disabled={!canProceed} onClick={onNext}>
          Continue
        </button>
      </div>

      <PhotoPickerModal
        show={showPhotoPicker}
        onClose={() => setShowPhotoPicker(false)}
        onSelect={(source) => {
          setShowPhotoPicker(false);
          fileRef.current.removeAttribute('capture');
          fileRef.current.click();
        }}
      />
    </>
  );
}
